import React from 'react';
import { PropTypes } from 'prop-types';
import { compose, pure } from 'recompose';
import LoadSpinner from 'components/LoadSpinner';

import KeyBoardSelector from 'components/KeyBoardSelector';
import ItemAddress from './Items/Address';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';
import { LIST, MODE } from './constants';
import 'react-virtualized/styles.css';
import styled, { css } from 'styled-components';

import SoFormButton from 'refactored/features/soForm/components/other/SoFormButton';
import { AddIcon, EditIcon } from 'refactored/ui/icons';
import { useTranslation } from 'react-i18next';

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* padding: 8px 16px; */
  gap: 10px;

  height: 32px;

  /* grey / 200 */

  background: #e2e8ee;
  border-radius: 4px 4px 0px 0px;
`;

const HeaderText = styled.div`
  height: 16px;
  margin: 8px 16px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  /* identical to box height, or 114% */

  text-transform: uppercase;

  /* grey / 700 */

  color: #54708c;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 8px;

  height: 56px;

  /* grey / 200 */

  background: #e2e8ee;
  border-radius: 0px 0px 4px 4px;
  ${(props) =>
    !!props.hasMultipleButtons &&
    css`
      ${SoFormButton} {
        width: 50%;
      }
    `};
`;

const enhance = compose(pure);

const AddressSelector = ({
  className,
  address,
  options,
  mode,
  selectAddress,
  loading,
  listenerElement,
  createAddress,
  modifyAddress,
  quoteWithZipcode,
  loadMoreAddresses,
  totalAddresses,
  hasError,
  handleIsRowLoaded,
  t,
}) => {
  const rowRenderer = ({ key, index, style }) => (
    <ItemAddress
      key={key}
      value={options[index]}
      style={style}
      onClick={selectAddress}
    />
  );

  const canEdit = mode === MODE.SELECTED && !loading && !hasError;
  const showZipCode = mode === MODE.ZIPCODE && !loading && !hasError;

  const { t: catalogT } = useTranslation('Catalog');

  return (
    <KeyBoardSelector listenerElement={listenerElement} className={className}>
      <HeaderContainer>
        <HeaderText>
          {catalogT('configuration.addresses.saved_addresses')}
        </HeaderText>
      </HeaderContainer>

      {/* No results */}
      {mode === MODE.SEARCH && !loading && options?.length === 0 && (
        <HeaderText>{t('empty_addresses')}</HeaderText>
      )}

      {/* Selected item */}
      {mode === MODE.SELECTED && !loading && !hasError && (
        <div className="address-selector__selected">
          <ItemAddress value={address} onClick={selectAddress} />
        </div>
      )}

      {/* List */}
      {true && mode === MODE.SEARCH && (
        <div className="address-selector__list">
          <AutoSizer disableHeight>
            {({ width }) =>
              options?.length > 0 && (
                <InfiniteLoader
                  isRowLoaded={handleIsRowLoaded}
                  loadMoreRows={loadMoreAddresses}
                  rowCount={totalAddresses}
                  threshold={20}
                  minimumBatchSize={10}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <List
                      onRowsRendered={onRowsRendered}
                      ref={registerChild}
                      rowCount={totalAddresses}
                      rowRenderer={rowRenderer}
                      rowHeight={LIST.ROW_HEIGHT}
                      width={width}
                      height={LIST.LIST_HEIGHT}
                    />
                  )}
                </InfiniteLoader>
              )
            }
          </AutoSizer>
        </div>
      )}

      {/* Loading spinner */}
      {loading && (
        <div className="loading">
          <LoadSpinner
            loadText={t('load_text')}
            loadTitle={t('load_title')}
            skin="center"
          />
        </div>
      )}

      <FooterContainer hasMultipleButtons={canEdit}>
        {showZipCode ? (
          <SoFormButton
            variant={'small'}
            inverse={true}
            onClick={quoteWithZipcode}
          >
            {catalogT('configuration.addresses.get_price_for_zipcode')}{' '}
            {address.zipcode}
          </SoFormButton>
        ) : (
          <>
            {canEdit && (
              <SoFormButton
                variant={'small'}
                inverse={true}
                onClick={modifyAddress}
              >
                <EditIcon />
                {catalogT('configuration.addresses.edit_address')}
              </SoFormButton>
            )}
            <SoFormButton variant={'small'} onClick={createAddress}>
              <AddIcon /> {catalogT('configuration.addresses.new_address')}
            </SoFormButton>
          </>
        )}
      </FooterContainer>
    </KeyBoardSelector>
  );
};

AddressSelector.propTypes = {
  className: PropTypes.string,
  address: PropTypes.shape({
    isZipcode: PropTypes.bool,
  }),
  selectAddress: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  listenerElement: PropTypes.instanceOf(Element).isRequired,
  createAddress: PropTypes.func.isRequired,
  modifyAddress: PropTypes.func.isRequired,
  quoteWithZipcode: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  inputValue: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.string.isRequired,
  loadMoreAddresses: PropTypes.func.isRequired,
  totalAddresses: PropTypes.number.isRequired,
  handleIsRowLoaded: PropTypes.func.isRequired,
};

AddressSelector.defaultProps = {
  loading: false,
  hasError: false,
  inputValue: '',
};

export default enhance(AddressSelector);
